import { Routes, Route, Navigate, useSearchParams } from "react-router-dom"
import { AccessRoute, HomeRedirect } from "./components/AccessRoute/AccessRoute"
import Layout from "./components/Layout/Layout"
import { ProtectedRoute } from "./components/ProtectedRoute/ProtectedRoute"
import { useAuth } from "./contexts/AuthContext"
import AuthCallback from "./pages/AuthCallback/AuthCallback"
import AuthLogout from "./pages/AuthLogout/AuthLogout"
import Login from "./pages/Login/Login"

import AuditReportsPage from "./pages/AuditReports/AuditReportsPage"
import DaeDashboard from "./pages/DaeDashboard/DaeDashboard"
import AllDTAsView from "./pages/DTADashboard/AllDTAsView"
import CompareVersionsPage from "./pages/DTADashboard/CompareVersionsPage"
import DTADetailsView from "./pages/DTADashboard/DataStreamDetails"
import MyStudies from "./pages/MyStudies/MyStudies"
import NotFound from "./pages/NotFound/NotFound"
import NotificationsPage from "./pages/Notifications/NotificationsPage"
import PrincipalDetail from "./pages/Principals/PrincipalDetail"
import PrincipalDirectory from "./pages/Principals/PrincipalDirectory"
import RunDetails from "./pages/RunDetails/RunDetails"
import ProtocolForm from "./pages/StudyDetails/Protocol/ProtocolForm"
import StudyDetails from "./pages/StudyDetails/StudyDetails"
import VendorDashboard from "./pages/VendorDashboard/VendorDashboard"
import VendorDataSubmission from "./pages/VendorDataSubmission/VendorDataSubmission"
import VendorDetail from "./pages/VendorManagement/VendorDetail"
import VendorManagement from "./pages/VendorManagement/VendorManagement"
import { ROUTES } from "./utils/constants"
import { readRedirectParam } from "./utils/redirect"

function App() {
  const { user, loading } = useAuth()

  // Redirect authenticated users away from login page
  const LoginRoute = () => {
    const [searchParams] = useSearchParams()
    if (loading) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-gray-600">Loading...</div>
        </div>
      )
    }
    if (user?.isAuthenticated) {
      // Already signed in and following a deep link (e.g. a grant email): honour its target. This
      // reads the param straight off the URL rather than the sessionStorage store/consume path
      // because the user never left the app, so there is no IDP round trip to carry it across.
      const redirect = readRedirectParam(searchParams)
      return <Navigate to={redirect ?? ROUTES.HOME} replace />
    }
    return <Login />
  }

  return (
    <Routes>
      <Route path={ROUTES.LOGIN} element={<LoginRoute />} />
      <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallback />} />
      <Route path={ROUTES.LOGOUT} element={<AuthLogout />} />
      <Route path="/" element={<Layout />}>
        <Route
          index
          element={
            <ProtectedRoute>
              <HomeRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendor/dashboard"
          element={
            <ProtectedRoute>
              <AccessRoute page="vendor">
                <VendorDashboard />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route path="dae" element={<Navigate to="/dae/dashboard" replace />} />
        <Route
          path="dae/dashboard"
          element={
            <ProtectedRoute>
              <AccessRoute page="dashboard">
                <DaeDashboard />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="dae/audit-reports"
          element={
            <ProtectedRoute>
              <AccessRoute page="audit_reports">
                <AuditReportsPage />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="studies"
          element={
            <ProtectedRoute>
              <AccessRoute page="studies">
                <MyStudies />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <ProtectedRoute>
              <AccessRoute page="vendors">
                <VendorManagement />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors/:vendorId"
          element={
            <ProtectedRoute>
              <AccessRoute page="vendors">
                <VendorDetail />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="principals"
          element={
            <ProtectedRoute>
              <AccessRoute page="people">
                <PrincipalDirectory />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="principals/:principalId"
          element={
            <ProtectedRoute>
              <AccessRoute page="people">
                <PrincipalDetail />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        {/* Studies Routes */}
        <Route
          path="studies/:studyId"
          element={
            <ProtectedRoute>
              <AccessRoute page="studies">
                <StudyDetails />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="studies/:studyId/runs/:runId"
          element={
            <ProtectedRoute>
              <AccessRoute>
                <RunDetails />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="studies/:studyId/protocol/edit"
          element={
            <ProtectedRoute>
              <AccessRoute page="studies">
                <ProtocolForm />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        {/* Notifications Routes */}
        <Route
          path="notifications"
          element={
            <ProtectedRoute>
              <AccessRoute>
                <NotificationsPage />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        {/* DTA Routes */}
        <Route
          path="dtas"
          element={
            <ProtectedRoute>
              <AccessRoute page="dtas">
                <AllDTAsView />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        {/* Redirect old /dtas/create to study page with modal */}
        <Route path="studies/:studyId/dtas/create" element={<Navigate to=".." replace />} />
        <Route
          path="dtas/:dtaId/compare"
          element={
            <ProtectedRoute>
              <AccessRoute>
                <CompareVersionsPage />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="dtas/:dtaId"
          element={
            <ProtectedRoute>
              <AccessRoute>
                <DTADetailsView />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="dtas/:dtaId/v/:version"
          element={
            <ProtectedRoute>
              <AccessRoute>
                <DTADetailsView />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        {/* Data Submission Routes */}
        <Route
          path="dtas/:dtaId/submit"
          element={
            <ProtectedRoute>
              <AccessRoute>
                <VendorDataSubmission />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="vendor/submit/:dtaId"
          element={
            <ProtectedRoute>
              <AccessRoute page="vendor">
                <VendorDataSubmission />
              </AccessRoute>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}

export default App
